import React, { useState } from 'react'

import { useDashboardWidgetActions } from 'redux/actions/Widgets/DashboardWidgets'
import { ILayoutItem } from 'types/GlobalDashboard'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'

import LoadingSection from 'components_new/molecules/LoadingSection'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import DashboardTreeSelector from 'components_new/organisms/DashboardTreeSelector'

import {
  FormattedWidgetData,
  TCoreWidgetFullyLoaded,
  WidgetType
} from 'types/GlobalWidget'

import PreviewWidget from './PreviewWidget'

import { useDashboard } from 'redux/hooks/Dashboards'
import { useTranslation } from 'language/translator'
import { useCustomization } from 'redux/hooks/Customization'

interface CopyToDashboardDialog {
  formattedData: FormattedWidgetData
  open: boolean
  onClose: () => void
  widget: TCoreWidgetFullyLoaded
}

const CopyToDashboardDialog = (props: CopyToDashboardDialog) => {
  const { formattedData, onClose, open, widget } = props
  const [copying, setCopying] = useState<boolean>(false)
  const [dashboard, setDashboard] = useState<string | null>(null)

  const customization = useCustomization()

  const { tryCreateDashboardWidget } = useDashboardWidgetActions()

  const { t } = useTranslation()

  const selectedDashboard = useDashboard(dashboard)

  const handleCopy = () => {
    setCopying(true)

    if (selectedDashboard) {
      const availableSpace = selectedDashboard.available_space as ILayoutItem

      tryCreateDashboardWidget(
        {
          dashboard_id: selectedDashboard.id,
          kpi_id: null,
          widget_id: widget.id,
          width: availableSpace.w,
          height: widget.type === WidgetType.TEXT ? 1 : availableSpace.h,
          x: availableSpace.x,
          y: availableSpace.y
        },
        (err) => {
          if (!err) {
            setDashboard(null)
            onClose()
          }
          setCopying(false)
        }
      )
    }

    setDashboard(null)
  }

  const handleCloseDialog = () => {
    onClose()
    setCopying(false)
    setDashboard(null)
  }

  return (
    <>
      {/*-- menus --*/}
      <SimpleDialog
        actions={
          copying ? null : (
            <>
              <Button
                disabled={copying}
                onClick={handleCloseDialog}
                variant="text"
              >
                {t('global', 'cancel')}
              </Button>
              <Button disabled={copying || !dashboard} onClick={handleCopy}>
                {t('global', 'copy_to')}
              </Button>
            </>
          )
        }
        contentText={
          copying
            ? undefined
            : 'Välj den dashboard som du vill att din widget ska kopieras till.'
        }
        maxWidth="xs"
        onClose={handleCloseDialog}
        open={open}
        title={t('global', 'copy_to')}
      >
        <Box
          sx={{
            position: 'relative'
          }}
        >
          {copying ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
                bgcolor: 'rgba(255, 255, 255, 0.92)'
              }}
            >
              <LoadingSection
                description={['Kopierar till dashboard']}
                sx={{ py: 2 }}
              />
            </Box>
          ) : null}

          <PreviewWidget
            customization={customization}
            formattedData={formattedData}
            widget={widget}
          />

          <Collapse in={!copying}>
            <DashboardTreeSelector
              selected={dashboard}
              setSelected={setDashboard}
              type="tab"
            />
          </Collapse>
        </Box>
      </SimpleDialog>
    </>
  )
}

export default CopyToDashboardDialog
